import type { FunctionComponent } from "react";
import { useMemo } from "react";
import type { PreloadedQuery } from "react-relay";
import { usePreloadedQuery } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { useSearchContext } from "scmp-app/components/search/contexts";
import { query } from "scmp-app/components/search/search-result-panel";
import { DropdownMenu } from "scmp-app/components/search/search-result-panel/components/dropdown-menu";
import type { ContentTypeEnum } from "scmp-app/queries/__generated__/searchArticleResultPaginationQuery.graphql";
import type { searchResultPanelQuery } from "scmp-app/queries/__generated__/searchResultPanelQuery.graphql";

import type { TypeOption } from "./const";
import { getTypeOptionsWithCount } from "./helpers";

const ALL_CONTENT_TYPES: ContentTypeEnum[] = ["ARTICLE", "VIDEO", "GALLERY"];

type Props = {
  className?: string;
  queryReference: PreloadedQuery<searchResultPanelQuery>;
};

export const TypeMenu: FunctionComponent<Props> = ({ className, queryReference }) => {
  const { articleTypeIds, contentTypes, setArticleTypeIds, setContentTypes } = useSearchContext();

  const data = usePreloadedQuery(query, queryReference);

  const options = useMemo(() => getTypeOptionsWithCount(data), [data]);

  const isChangedOptionColor = useMemo(() => {
    if (notEmpty(articleTypeIds)) return true;

    return (
      !Array.isArray(contentTypes) ||
      contentTypes.length !== ALL_CONTENT_TYPES.length ||
      !contentTypes.every(type => ALL_CONTENT_TYPES.includes(type))
    );
  }, [articleTypeIds, contentTypes]);

  const handleOptionSelect = (typeOption: TypeOption) => {
    if (typeOption.isContentType) {
      setArticleTypeIds?.([]);
      setContentTypes?.(typeOption.value as ContentTypeEnum[]);
    } else {
      setArticleTypeIds?.(typeOption.value);
      setContentTypes?.([]);
    }
  };

  return (
    <DropdownMenu<string[], TypeOption>
      className={className}
      isChangedOptionColor={isChangedOptionColor}
      onOptionSelect={handleOptionSelect}
      options={options}
      placeholder="Type"
      selectedOptionValue={notEmpty(articleTypeIds) ? articleTypeIds : contentTypes}
      withCount={!!options}
    />
  );
};

TypeMenu.displayName = "TypeMenu";
